import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Key, ArrowLeft } from 'lucide-react'

export function RefundPolicy() {
  return (
    <>
      <Helmet>
        <title>Refund Policy | Twenty</title>
        <meta name="description" content="All purchases are final. No refunds under any circumstances." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border">
          <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              <span className="font-semibold">Twenty</span>
            </Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-semibold mb-8">Refund Policy</h1>
          
          <div className="prose prose-neutral max-w-none">
            <p className="text-muted-foreground mb-6">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">All Sales Final</h2>
              <p className="text-muted-foreground mb-4">
                <strong>All purchases are final and non-refundable.</strong> By subscribing to Twenty, 
                you are purchasing pre-paid growth capacity and infrastructure access. This operates 
                as a capacity reservation model - once resources are allocated to your account, they 
                cannot be resold, reassigned, or refunded.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">No Refunds for Any Reason</h2>
              <p className="text-muted-foreground mb-4">
                We do not offer refunds under any circumstances, including but not limited to:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Change of mind or no longer needing the service</li>
                <li>Finding a different solution or competitor</li>
                <li>Technical difficulties (though we will provide support)</li>
                <li>Business closure or project cancellation</li>
                <li>Unused capacity or low usage months</li>
                <li>Service cancellation mid-billing cycle</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">24-Hour Grace Period</h2>
              <p className="text-muted-foreground mb-4">
                You may request a refund within <strong>24 hours of purchase</strong> if your account 
                has remained completely unused. "Unused" means:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>No API calls have been made</li>
                <li>No API keys have been created</li>
                <li>No data has been stored or uploaded</li>
                <li>No infrastructure resources have been consumed</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Refunds under this grace period are granted at our sole discretion. Contact 
                billing@twenty.sh immediately with your request. Once 24 hours have passed or 
                if any account activity has occurred, this grace period expires and our standard 
                no-refund policy applies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">Chargebacks and Account Closure</h2>
              <p className="text-muted-foreground mb-4">
                <strong>Initiating a chargeback or payment dispute with your bank, credit card 
                company, or payment provider will result in immediate and permanent account closure.</strong> 
                This includes:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Immediate termination of all services</li>
                <li>Permanent ban from the platform</li>
                <li>Deletion of all account data and stored assets</li>
                <li>Forfeiture of any remaining subscription time</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                We reserve the right to pursue legal action to recover fees, costs, and damages 
                associated with fraudulent chargebacks. If you have a legitimate billing concern, 
                contact us directly at billing@twenty.sh - we are committed to resolving issues 
                without resorting to payment disputes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">Legal Compliance</h2>
              <p className="text-muted-foreground">
                Twenty aims to be fully compliant with all applicable laws and regulations in 
                jurisdictions where we operate. We will work to comply with legal requirements where 
                possible, including consumer protection laws. Nothing in this refund policy is intended 
                to override your statutory rights where such rights cannot be excluded by contract. 
                If you believe this policy violates applicable law in your jurisdiction, please 
                contact legal@twenty.sh.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">Billing Disputes</h2>
              <p className="text-muted-foreground">
                If you believe you were billed in error (incorrect amount, duplicate charge, 
                unauthorized charge),                 contact us at billing@twenty.sh within 30 days of the 
                charge. We will review and resolve legitimate billing errors only. Disputes about 
                service quality or suitability do not qualify for refunds.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium mb-4">Cancellation</h2>
              <p className="text-muted-foreground">
                You may cancel your subscription at any time through your account settings or by 
                contacting support. Cancellation takes effect at the end of your current billing 
                period. You will not be charged for subsequent periods, but <strong>no refunds 
                will be issued for the current billing period or any previous periods</strong>, 
                regardless of your usage level during those periods.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium mb-4">Contact</h2>
              <p className="text-muted-foreground">
                Questions about this policy? Email us at legal@twenty.sh
              </p>
            </section>
          </div>
        </main>

        <footer className="border-t border-border py-8">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Twenty
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}
